import get from 'lodash/get';
import omit from 'lodash/omit';
import trim from 'lodash/trim';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import map from 'lodash/map';
import capitalize from 'lodash/capitalize';
import HtmlToReact from 'html-to-react';

import hiringEventsAPI from './api/hiringEventAPI';
import {
  EVENT_JOB_DETAILS_INITIAL_ERRORS_STATE,
  EVENT_JOB_DETAILS_OPTIONAL_FIELDS,
  JOB_DETAIL_ERROR_MESSAGES,
  JOB_DETAIL_VALIDATE_FIELDS_FOR_VALID_CHARS,
  VENDOR_NAMES,
} from './constants/hiringEvents';
import { toSentenceCase } from './utils';
import {
  containsInvalidCharacters,
  getInvalidCharacters,
  getInvalidCharactersErrorMessage,
  isValidHttpUrl,
} from './validation';
import { getMediaUrl } from './mediaService';
import { ENTITIES_TYPE, MEDIA_SIZES } from './constants/mediaService';

const doesStringContainDigitsOnly = (value) => {
  const re = /^[0-9\b]+$/;
  return re.test(value);
};

const doesStringContainDigitsOrDecimals = (value) => {
  const re = /^[+-]?([0-9]+\.?[0-9]*|\.[0-9]+)$/;
  return re.test(value);
};

const getVendorNameOptions = event => map(keys(VENDOR_NAMES), (val, index) => (
  {
    id: index,
    title: capitalize(VENDOR_NAMES[val]),
    selected: get(event, 'external_vendor') === VENDOR_NAMES[val],
    hovered: false,
    key: VENDOR_NAMES[val] === 'None' ? '' : VENDOR_NAMES[val],
  }
));

const boolToTruth = bool => (bool ? 'Yes' : 'No');

const isUUID = string => string?.match('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');

const validateJob = (job) => {
  let isValidJob = true;
  const errorMessages = {};
  const errors = Object.assign({}, EVENT_JOB_DETAILS_INITIAL_ERRORS_STATE);
  const totalEmptyFields = Object.keys(omit(job, EVENT_JOB_DETAILS_OPTIONAL_FIELDS)).filter(val => !trim(job[val]));

  totalEmptyFields.forEach((val) => {
    errors[val] = true;
    errorMessages[val] = JOB_DETAIL_ERROR_MESSAGES[val]?.required;
  });

  if (totalEmptyFields.length) {
    isValidJob = false;
  }

  JOB_DETAIL_VALIDATE_FIELDS_FOR_VALID_CHARS.forEach((val) => {
    const inValidChar = containsInvalidCharacters(job[val]);
    if (inValidChar) {
      const invalidChars = getInvalidCharacters(job[val]);
      isValidJob = false;
      errors[val] = true;
      errorMessages[val] = getInvalidCharactersErrorMessage(invalidChars);
    }
  });

  const jobUrl = get(job, 'job_url');
  if (!isEmpty(trim(jobUrl)) && !isValidHttpUrl(trim(jobUrl))) {
    errors.job_url = true;
    errorMessages.job_url = JOB_DETAIL_ERROR_MESSAGES.job_url?.invalid;
    isValidJob = false;
  }
  return { isValidJob, errors, errorMessages };
};

const getEventRegistrantsInCSV = async (eventId) => {
  const response = await hiringEventsAPI().getEventRegistrantsInCSV(eventId);
  return response;
};

const getRegistrantDetails = async (registrantId) => {
  const response = await hiringEventsAPI().getRegistrantDetails(registrantId);
  return response;
};

const getRegistrantResume = async (registrantId, resumeFilename, resumeUri) => {
  const response = await hiringEventsAPI().getRegistrantResume(registrantId, resumeFilename, resumeUri);
  return response;
};

const downloadEventRegistrants = async (eventId) => {
  const extractLines = text => text.split('\n');
  const extractColumnValues = text => text.split(',');
  const response = await hiringEventsAPI().getEventRegistrantsInCSV(eventId);
  const lines = extractLines(response);
  const data = [];
  try {
    data.push([lines[0]]);
    data.push([lines[1]]);
    // eslint-disable-next-line no-plusplus
    for (let i = 2; i < lines.length - 1; i++) {
      data.push([...extractColumnValues(lines[i])]);
    }
    return data;
  } catch {
    if (data.length === 0) {
      const noRegistrantsError = new Error('No registrants');
      noRegistrantsError.name = 'NoRegistrantDataError';
      throw noRegistrantsError;
    }
    return null;
  }
};

const getArticleForText = (name) => {
  if (name && /[aeiou]/i.test(name[0])) {
    return 'an';
  }
  return 'a';
};

const getRequiredJobParams = jobs => jobs.map((job) => {
  const employmentType = get(job, 'employment_type');
  const jobTitle = get(job, 'job_title');
  const jobUrl = get(job, 'job_url');
  const location = get(job, 'location');
  const salary = get(job, 'salary');
  const sequenceId = get(job, 'sequence_id');
  const siteId = get(job, 'site_id');

  return {
    employment_type: employmentType,
    job_title: jobTitle,
    job_url: !isEmpty(jobUrl) ? jobUrl : null,
    location,
    salary: !isEmpty(salary) ? salary : null,
    sequence_id: sequenceId,
    site_id: !isEmpty(siteId) ? siteId : null,
  };
});

// LUT-538: delegates to the shared helper so virtual-event links follow the same rules as
// job and image URLs on the backend.
const isValidURL = url => isValidHttpUrl(url);

const htmlToReactParser = (text = '') => {
  const HtmlToReactParser = HtmlToReact.Parser;
  return new HtmlToReactParser().parse(text);
};

const toStartCase = str => (str ? str.toLowerCase().split(' ').map(word => toSentenceCase(word)).join(' ') : null);

const isObjectEmpty = obj => !obj || !Object.keys(obj).length;

const extractVendorId = str => (str && str.split('/', -1).pop());

const extractVendorName = (str) => {
  const hostname = str.split('/', -1)[2];
  let vendorName;
  if (hostname?.includes('jobcase.typeform.com')) {
    vendorName = 'TYPEFORM';
  }
  if (hostname?.includes('app.brazenconnect.com')) {
    vendorName = 'BRAZEN';
  }

  return vendorName;
};

const shouldExtractVendorData = (inputLink, data) => {
  const hostname = inputLink?.split('/', -1)[2];
  if (hostname?.includes('jobcase.typeform.com') || hostname?.includes('app.brazenconnect.com')) {
    const eventType = data?.event_type;
    const vendorName = data?.external_vendor;
    let isValidEvent;
    switch (eventType) {
      case 'PHONE_INTERVIEW':
      case 'VIDEO_CHAT':
      case 'ONLINE_CHAT':
        isValidEvent = true;
        break;
      default:
        isValidEvent = false;
        break;
    }
    const isNotEmployer = vendorName !== 'EMPLOYER';
    const isBrazenEventsLink = inputLink?.split('/', -1)[3] === 'events';

    return isValidEvent && isNotEmployer && !isBrazenEventsLink;
  }
  return false;
};

export const copyImagesFromOrg = ({
  assets,
  updatedEvent,
  isDuplicate = false,
  isEdit = false,
}) => {
  let images;

  if (isEdit || isDuplicate) {
    images = updatedEvent?.images?.map((asset) => {
      const data = {
        image_type: asset.image_type,
        image_url: asset.image_url,
      };
      if (isEdit && asset.hiring_event_id) {
        data.hiring_event_id = asset.hiring_event_id;
      }
      return data;
    });
  } else if (!assets || !assets.length) {
    images = [];
  } else {
    images = assets.map((asset) => {
      const data = {
        image_type: ENTITIES_TYPE.PROMOTIONAL,
        image_url: getMediaUrl(asset?.media_id, MEDIA_SIZES.ORIGINAL),
      };
      return data;
    });
  }

  return images
    .map((asset, index) => ({ ...asset, display_order: index + 1 }))
    .sort((x, y) => x.display_order - y.display_order);
};

export {
  doesStringContainDigitsOnly,
  doesStringContainDigitsOrDecimals,
  isUUID,
  isValidURL,
  boolToTruth,
  validateJob,
  getEventRegistrantsInCSV,
  getRegistrantDetails,
  getRegistrantResume,
  downloadEventRegistrants,
  getArticleForText,
  getRequiredJobParams,
  getVendorNameOptions,
  htmlToReactParser,
  toStartCase,
  isObjectEmpty,
  extractVendorId,
  extractVendorName,
  shouldExtractVendorData,
};

export default {
  doesStringContainDigitsOnly,
  doesStringContainDigitsOrDecimals,
  isUUID,
  isValidURL,
  boolToTruth,
  validateJob,
  getEventRegistrantsInCSV,
  getRegistrantDetails,
  getRegistrantResume,
  downloadEventRegistrants,
  getArticleForText,
  getRequiredJobParams,
  getVendorNameOptions,
  htmlToReactParser,
  toStartCase,
  isObjectEmpty,
  extractVendorId,
  extractVendorName,
  shouldExtractVendorData,
};
